/**
 * useSandboxRequest.ts — tenant-side sandbox request data hooks.
 *
 * decision-2026-07-25-sandbox-request-tenant-broker +
 * decision-2026-07-25-sandbox-request-approval-quota (quota-bounded auto-approve,
 * per-tenant ceiling 3; over-quota diverts to the meta-broker operator queue).
 *
 *   POST /api/broker/v1/sandbox-requests            submit (Gate-0 validated server-side)
 *   GET  /api/broker/v1/sandbox-requests/mine       the submitter's own requests + state
 *   GET  /api/broker/v1/sandbox-requests/queue      operator queue (meta broker only)
 *   POST /api/broker/v1/sandbox-requests/:id/approve   over-quota → emit
 *   POST /api/broker/v1/sandbox-requests/:id/decline   over-quota → reason, returned
 *
 * Boundary discipline (adr-075 D11): this module submits and reads state. There is
 * deliberately no provision, realm or decommission call anywhere in it — those live
 * in useProvisioning.ts, which the tenant build never reaches.
 *
 * The tenant a request is booked against is NEVER sent in the body. The server takes
 * it from the Keycloak token. Sending it would make the field a trust boundary.
 */

import { useCallback, useEffect, useReducer } from 'react';
import type { DisclosedCostBasis } from '../components/CostDisclosure';

export type AppKind = 'platform' | 'external';

/** Gate-0 fields (convention-app-onboarding / ADR-068, ADR-067 Amd 2). */
export interface SandboxRequestInput {
  app_name: string;
  slug: string;
  purpose: string;
  app_kind: AppKind;
  /** Required when app_kind === 'external'; omitted otherwise. */
  github_org?: string;
  /** What was actually shown to the human who ticked the box. */
  disclosed_cost_basis: DisclosedCostBasis;
}

export type SandboxRequestState =
  | 'pending_operator'
  | 'approved'
  | 'provisioning'
  | 'ready'
  | 'declined';

export interface SandboxRequestRow {
  id: string;
  tenant: string;
  app_name: string;
  slug: string;
  purpose: string;
  app_kind: AppKind;
  github_org: string | null;
  state: SandboxRequestState;
  /** Set when over-quota: the request queued instead of emitting. */
  quota_position: number | null;
  decline_reason: string | null;
  requested_by: string;
  created_at: string;
}

export interface SubmitResult {
  request: SandboxRequestRow;
  /** true when the quota admitted it and sandbox.provision_requested was emitted. */
  emitted: boolean;
}

async function readError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
  return body.message ?? body.error ?? `request failed (HTTP ${res.status})`;
}

/** Submit a sandbox request. Throws with the server's message on any non-2xx. */
export async function submitSandboxRequest(
  token: string,
  input: SandboxRequestInput,
): Promise<SubmitResult> {
  const res = await fetch('/api/broker/v1/sandbox-requests', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as SubmitResult;
}

// ── The submitter's own requests (state display) ─────────────────────────────

export type MyRequestsState =
  | { tag: 'idle' }
  | { tag: 'loading' }
  | { tag: 'loaded'; requests: SandboxRequestRow[] }
  | { tag: 'error'; message: string };

type Action =
  | { type: 'START' }
  | { type: 'SUCCESS'; requests: SandboxRequestRow[] }
  | { type: 'ERROR'; message: string };

function reducer(prev: MyRequestsState, a: Action): MyRequestsState {
  switch (a.type) {
    case 'START':
      return { tag: 'loading' };
    case 'SUCCESS':
      return { tag: 'loaded', requests: a.requests };
    case 'ERROR':
      return { tag: 'error', message: a.message };
    default:
      return prev;
  }
}

function useRequestList(token: string | null, path: string) {
  const [state, dispatch] = useReducer(reducer, { tag: 'idle' });

  const load = useCallback(() => {
    if (!token) return;
    dispatch({ type: 'START' });
    fetch(path, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) {
          dispatch({ type: 'ERROR', message: await readError(res) });
          return null;
        }
        return (await res.json()) as { requests: SandboxRequestRow[] };
      })
      .then((data) => {
        if (data) dispatch({ type: 'SUCCESS', requests: data.requests });
      })
      .catch(() => dispatch({ type: 'ERROR', message: 'NETWORK_ERROR' }));
  }, [token, path]);

  useEffect(() => {
    load();
  }, [load]);

  return { state, reload: load };
}

/** The submitter's own requests — tenant surface. */
export function useMySandboxRequests(token: string | null) {
  return useRequestList(token, '/api/broker/v1/sandbox-requests/mine');
}

/** The operator queue — meta broker surface. Mostly observational under auto-approve. */
export function useSandboxRequestQueue(token: string | null) {
  return useRequestList(token, '/api/broker/v1/sandbox-requests/queue');
}

// ── Operator actions (over-quota requests only) ──────────────────────────────

export async function approveSandboxRequest(token: string, id: string): Promise<void> {
  const res = await fetch(`/api/broker/v1/sandbox-requests/${id}/approve`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await readError(res));
}

export async function declineSandboxRequest(
  token: string,
  id: string,
  reason: string,
): Promise<void> {
  const res = await fetch(`/api/broker/v1/sandbox-requests/${id}/decline`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) throw new Error(await readError(res));
}
